import { useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Package,
  Plus,
  Trash2,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Skeleton } from '@/app/components/ui/skeleton';
import { Textarea } from '@/app/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  useGearRequestTemplate,
  useCreateRequestFromTemplate,
} from '@/hooks/use-gear-request-templates';
import { useProjects } from '@/hooks/use-projects';

interface EditableLine {
  description: string;
  quantity: number;
  notes: string;
}

export function GearRequestFromTemplatePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const templateId = parseInt(id || '0', 10);

  const { data: template, isLoading, isError } = useGearRequestTemplate(templateId);
  const { data: projectsData } = useProjects({ page_size: 100 });
  const createMutation = useCreateRequestFromTemplate();

  const projects = projectsData?.results || [];

  const [project, setProject] = useState<string>('');
  const [neededBy, setNeededBy] = useState('');
  const [priority, setPriority] = useState('normal');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<EditableLine[] | null>(null);

  const effectiveLines =
    lines ??
    (template?.lines.map((l) => ({
      description: l.description,
      quantity: l.quantity,
      notes: l.notes,
    })) || []);

  const updateLine = useCallback(
    (index: number, field: keyof EditableLine, value: string | number) => {
      const current = [...effectiveLines];
      current[index] = { ...current[index], [field]: value };
      setLines(current);
    },
    [effectiveLines]
  );

  const addLine = useCallback(() => {
    setLines([...effectiveLines, { description: '', quantity: 1, notes: '' }]);
  }, [effectiveLines]);

  const removeLine = useCallback(
    (index: number) => {
      setLines(effectiveLines.filter((_, i) => i !== index));
    },
    [effectiveLines]
  );

  const handleSubmit = useCallback(async () => {
    try {
      const validLines = effectiveLines
        .filter((l) => l.description.trim())
        .map((l) => ({
          description: l.description,
          quantity: l.quantity,
          notes: l.notes,
        }));
      if (validLines.length === 0) return;

      const result = await createMutation.mutateAsync({
        templateId,
        data: {
          project: project ? parseInt(project, 10) : undefined,
          needed_by: neededBy || undefined,
          priority,
          notes,
          lines: validLines,
        },
      });
      navigate(`/gear-requests/${result.id}`);
    } catch {
      // error handled by mutation
    }
  }, [templateId, project, neededBy, priority, notes, effectiveLines, createMutation, navigate]);

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !template) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="text-center text-red-600">
          Template not found or failed to load.
        </div>
        <Link to="/gear-requests/templates">
          <Button variant="link" className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Templates
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/gear-requests/templates">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Templates
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            New Request from &ldquo;{template.name}&rdquo;
          </h1>
          {template.description && (
            <p className="text-gray-600 mt-1">{template.description}</p>
          )}
        </div>
      </div>

      {/* Request details */}
      <Card>
        <CardHeader>
          <CardTitle>Request Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="project">Project</Label>
              <Select value={project} onValueChange={setProject}>
                <SelectTrigger>
                  <SelectValue placeholder="Select project..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Project</SelectItem>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="needed-by">Needed By</Label>
              <Input
                id="needed-by"
                type="date"
                value={neededBy}
                onChange={(e) => setNeededBy(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="priority">Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes for this request..."
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      {/* Editable lines */}
      <Card
        data-required="true"
        data-required-empty={
          effectiveLines.some((line) => line.description.trim()) ? 'false' : 'true'
        }
        data-required-message="Add at least one line item."
        tabIndex={-1}
      >
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Line Items (editable)</CardTitle>
            <Button variant="outline" size="sm" onClick={addLine}>
              <Plus className="h-3 w-3 mr-1" />
              Add Line
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {effectiveLines.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Package className="h-8 w-8 mx-auto mb-2 text-gray-400" />
              <p>No line items. Add items to this request.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-[1fr_80px_1fr_40px] gap-2 text-sm font-medium text-gray-500">
                <span>Description</span>
                <span>Qty</span>
                <span>Notes</span>
                <span />
              </div>
              {effectiveLines.map((line, index) => (
                <div
                  key={index}
                  className="grid grid-cols-[1fr_80px_1fr_40px] gap-2 items-start"
                >
                  <Input
                    value={line.description}
                    onChange={(e) =>
                      updateLine(index, 'description', e.target.value)
                    }
                    placeholder="Item description"
                  />
                  <Input
                    type="number"
                    value={line.quantity}
                    onChange={(e) =>
                      updateLine(
                        index,
                        'quantity',
                        parseInt(e.target.value, 10) || 1
                      )
                    }
                    min={1}
                  />
                  <Input
                    value={line.notes}
                    onChange={(e) =>
                      updateLine(index, 'notes', e.target.value)
                    }
                    placeholder="Notes"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeLine(index)}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Submit */}
      <div className="flex justify-end gap-3">
        <Link to="/gear-requests/templates">
          <Button variant="outline">Cancel</Button>
        </Link>
        <Button
          onClick={handleSubmit}
          disabled={createMutation.isPending}
        >
          <Package className="h-4 w-4 mr-2" />
          {createMutation.isPending ? 'Creating...' : 'Create Request'}
        </Button>
      </div>
    </div>
  );
}
